import axiosStatic, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

/* The body Foxx sends with every 4xx/5xx; `errorMessage` is the only message fit
 * for a user, axios' own `error.message` is generic. */
interface FoxxError {
  errorMessage?: string
}

type Tracked = InternalAxiosRequestConfig & { controller?: AbortController }

export const axios = axiosStatic.create()

const inFlight = new Set<AbortController>()

let unauthorized: (() => void) | undefined

/* What a 401 answer runs; the router registers the redirect to login. */
export function onUnauthorized(handler: () => void): void {
  unauthorized = handler
}

axios.interceptors.request.use((config) => {
  const controller = new AbortController()
  config.signal = controller.signal
  inFlight.add(controller)
  ;(config as Tracked).controller = controller
  return config
})

function forget(config?: InternalAxiosRequestConfig): void {
  const controller = (config as Tracked | undefined)?.controller
  if (controller) inFlight.delete(controller)
}

axios.interceptors.response.use(
  (response) => {
    forget(response.config)
    return response
  },
  (error: AxiosError<FoxxError>) => {
    forget(error.config)
    /* Still reject after redirecting to login, so the caller never takes the
       redirect for a response. */
    if (error.response?.status === 401) unauthorized?.()
    return Promise.reject(error)
  },
)

export function messageOf(error: unknown): string {
  const axiosError = error as AxiosError<FoxxError>
  if (axiosError?.response?.data?.errorMessage) return axiosError.response.data.errorMessage
  if (axiosError?.response?.status) return `${axiosError.response.status} ${axiosError.response.statusText}`
  if (axiosError?.message) return axiosError.message
  return 'Unexpected error'
}

export function isAborted(error: unknown): boolean {
  return axiosStatic.isCancel(error)
}

export function cancelActiveRequests(): void {
  inFlight.forEach((controller) => controller.abort())
  inFlight.clear()
}
